/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-params */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-use-before-define */
import type { ValidationMap, Requireable } from 'prop-types';
import PropTypes from 'prop-types';
import { tupleValidator } from './customPropTypes.js';
import {
  hasArguments,
  isArrayOf,
  isExact,
  isInstanceOf,
  isObjectOf,
  isOneOf,
  isOneOfType,
  isShape,
} from './describeGuards.js';
import type { ReactDescT, ReactDescObjT, InstanceOfT, OneOfT, ObjectOfT, ExactT } from './types.js';

const enrichPropType = (propType: Requireable<unknown>, reactDesc: ReactDescObjT): Requireable<unknown> => {
  const func = (props: Record<string, unknown>, propName: string, componentName: string, ...rest: [string, string]) => {
    if (reactDesc && reactDesc.deprecated && propName in props && !reactDesc.warned) {
      const { version = '', message = '' } = reactDesc.deprecated;
      const error = `Warning: "${propName}" property of "${componentName}" will be deprecated in version: ${version}.\n${message}`;
      console.warn(error);
      reactDesc.warned = true;
    }
    return propType(props, propName, componentName, ...rest);
  };
  Object.defineProperty(
    func,
    'isRequired',
    Object.getOwnPropertyDescriptor(propType, 'isRequired') as PropertyDescriptor,
  );
  return func as Requireable<unknown>;
};

const convertArray = (array: ReactDescT[]): Requireable<unknown>[] => array.map((type) => convertPropType(type));

const convertShape = (_shape: Record<string, ReactDescT>): ValidationMap<Record<string, unknown>> => {
  const result: ValidationMap<Record<string, unknown>> = {};
  Object.keys(_shape).forEach((key) => {
    result[key] = convertPropType(_shape[key]);
  });
  return result;
};

export const convertPropType = (propType: ReactDescT): Requireable<unknown> => {
  if (!propType || !propType.type) {
    throw new Error(`react-desc: unknown error -- proptype is not well defined`);
  }

  // DimSum PropTypes conversion
  if (propType.type === 'tuple') {
    return enrichPropType(
      PropTypes.arrayOf(tupleValidator(convertArray(propType.args as ReactDescT[]))),
      propType.reactDesc,
    );
  }

  // Default PropType conversion
  const realPropType = PropTypes[propType.type];

  if (!realPropType) {
    throw new Error(`react-desc: unknown type ${propType.type}`);
  }

  if (hasArguments(propType, realPropType)) {
    if (isOneOfType(propType, realPropType)) {
      return enrichPropType(realPropType(convertArray(propType.args as ReactDescT[])), propType.reactDesc);
    }
    if (isArrayOf(propType, realPropType)) {
      return enrichPropType(realPropType(convertPropType(propType.args as ReactDescT)), propType.reactDesc);
    }
    if (isShape(propType, realPropType)) {
      return enrichPropType(
        realPropType(convertShape(propType.args as Record<string, ReactDescT>)),
        propType.reactDesc,
      );
    }
    if (isInstanceOf(propType, realPropType)) {
      return enrichPropType(realPropType(propType.args as Parameters<InstanceOfT>[0]), propType.reactDesc);
    }
    if (isOneOf(propType, realPropType)) {
      return enrichPropType(realPropType(propType.args as Parameters<OneOfT>[0]), propType.reactDesc);
    }
    if (isObjectOf(propType, realPropType)) {
      return enrichPropType(realPropType(propType.args as Parameters<ObjectOfT>[0]), propType.reactDesc);
    }
    if (isExact(propType, realPropType)) {
      return enrichPropType(realPropType(propType.args as Parameters<ExactT>[0]), propType.reactDesc);
    }
    throw new Error(`react-desc: unknown error -- proptype with args is not matching`);
  }

  return enrichPropType(realPropType, propType.reactDesc);
};
